'use client'
import {
  TrackContentCardClickInfo,
  TrackMapCardClickInfo,
  TrackNavigationMenuClickInfo,
  TrackNavigationInfo,
} from './../types/analytics'
import { ParsedUrlQuery } from 'querystring'
// Note: Server-only types removed from client util
import analyticsService from '../analytics/analytics.instance'
import { DropdownOption, FormFieldState, FormProps } from '@studio/atomic-ui'
import { LeadRequest } from '../services/bff/leads'
import { BookingResult } from '../services/bff/bookings'
import { Product } from '../types/product'
import { getDurationDiscountCoupon } from './discounts'
import { DateRange } from '../types/dates'
import { CouponCode } from '../types/coupon'
import { CheckoutPrices } from '../types/prices'
import {
  GeneralForm,
  PriceInquiryForm,
  TrackToggleInfo,
  TrackTabInfo,
  TrackSpaceCardPropertyResultClickInfo,
  TrackWorkspaceCardPropestyResultClickInfo,
  TrackBuildingCardPropertyResultClickInfo,
} from '../types/analytics'
import dayjs from 'dayjs'
import { isValidHttpURL } from './helpers'

const UTM_FIELDS_KEY = 'utmFields'
const TRANSACTION_IDS_KEY = 'trackedTransactionIds'
const AB_CONTEXT_KEY = 'abTestingContext'

export type AbTestingContext = {
  variant: string
  cookieName: string
  value: string
}

export const setAbTestingContext = (context: AbTestingContext) => {
  try {
    sessionStorage.setItem(AB_CONTEXT_KEY, JSON.stringify(context))
  } catch {
    // ignore write errors
  }
}

export const getAbTestingContext = (): AbTestingContext | null => {
  try {
    const raw = sessionStorage.getItem(AB_CONTEXT_KEY)
    return raw ? (JSON.parse(raw) as AbTestingContext) : null
  } catch {
    return null
  }
}

const parseQueryToUtmFields = (query: ParsedUrlQuery) => ({
  ...(query.utm_source ? { utmSource: query.utm_source as string | undefined } : {}),
  ...(query.utm_campaign ? { utmCampaign: query.utm_campaign as string | undefined } : {}),
  ...(query.utm_medium ? { utmMedium: query.utm_medium as string | undefined } : {}),
  ...(query.utm_content ? { utmContent: query.utm_content as string | undefined } : {}),
  ...(query.utm_term ? { utmTerm: query.utm_term as string | undefined } : {}),
})

export const setUtmFields = (query: ParsedUrlQuery) => {
  const storedUtmFields = JSON.parse(sessionStorage.getItem(UTM_FIELDS_KEY) ?? '{}')
  sessionStorage.setItem(UTM_FIELDS_KEY, JSON.stringify({ ...storedUtmFields, ...parseQueryToUtmFields(query) }))
}

export const getUtmFields = (query: ParsedUrlQuery): Record<string, string> => {
  const storedUtmFields = JSON.parse(sessionStorage.getItem(UTM_FIELDS_KEY) ?? '{}')
  const queryUtmFields = parseQueryToUtmFields(query)
  return { ...storedUtmFields, ...queryUtmFields }
}

const isTransactionAlreadyTracked = (transactionId: string): boolean => {
  const trackedTransactions = JSON.parse(localStorage.getItem(TRANSACTION_IDS_KEY) || '[]')
  return trackedTransactions.includes(transactionId)
}

const addTransactionToTracked = (transactionId: string) => {
  const trackedTransactions = JSON.parse(localStorage.getItem(TRANSACTION_IDS_KEY) || '[]')
  trackedTransactions.push(transactionId)
  localStorage.setItem(TRANSACTION_IDS_KEY, JSON.stringify(trackedTransactions))
}

export const trackTourForm = (form: FormProps, state: FormFieldState, request: LeadRequest) => {
  const tourBuildingOption = state.tourSchedule?.value as DropdownOption
  const location_id =
    (tourBuildingOption?.id && analyticsService?.getProductLocationId(tourBuildingOption.id, 'tripleseat')?.trim()) ||
    'not available'

  analyticsService?.trackTourForm({
    event: 'contact_tour_request_form_submitted',
    event_origin: 'front-end',
    event_vars: {
      form_name: form?.name?.toLowerCase() as string,
      form_type: 'tour request',
      form_id: form?.id as string,
      event_action: state.newsletter?.value ? 'subscribed for updates' : 'did not subscribe for updates',
      property_title: request.locationName ? request.locationName.toLowerCase() : 'none selected',
      location_id,
      category_descriptor: request.inquiryProductType
        ? request.inquiryProductType.toLowerCase().split(';').join(' | ')
        : 'none selected',
    },
  })
}

export const trackPurchaseEvents = (
  booking: BookingResult,
  product: Product,
  coupon: CouponCode,
  prices: CheckoutPrices,
  dateRange: DateRange
) => {
  const transactionId = booking.ids[0].toString()
  if (isTransactionAlreadyTracked(transactionId)) return
  const durationDiscountCoupon = getDurationDiscountCoupon(prices, dateRange)
  const productPrice = getTotalPriceFromProduct(prices) || 0
  const itemInfo = getItemInfo(product, coupon, dateRange, durationDiscountCoupon, productPrice)

  analyticsService?.trackPurchaseEvent({
    event: 'purchase',
    ecommerce: {
      transaction_id: transactionId,
      value: productPrice,
      tax: product.price?.taxAmount ?? 0,
      currency: product.price?.currency as string,
      coupon: addCouponsToAnalytics(coupon.code, durationDiscountCoupon),
      items: [itemInfo],
    },
  })

  const {
    product_name = '',
    product_type = '',
    product_capacity,
    product_hourly_rate,
    product_source,
    product_id,
    location_id,
  } = itemInfo
  analyticsService?.trackPurchaseCustom({
    event: 'purchase_custom_ts',
    event_origin: 'front-end',
    event_vars: {
      transaction_id: transactionId,
      value: productPrice,
      product_name,
      product_type,
      product_capacity,
      product_quantity_custom_ts: 1,
      product_hourly_rate,
      product_source,
      product_id,
      location_id,
    },
  })
  addTransactionToTracked(transactionId)
}

export const trackPaymentInfo = (
  booking: BookingResult,
  product: Product,
  coupon: CouponCode,
  prices: CheckoutPrices,
  dateRange: DateRange
) => {
  const durationDiscountCoupon = getDurationDiscountCoupon(prices, dateRange)
  const productPrice = getTotalPriceFromProduct(prices) || 0
  analyticsService?.trackPaymentInfo({
    event: 'add_payment_info',
    ecommerce: {
      currency: product.price?.currency || '',
      value: productPrice,
      coupon: addCouponsToAnalytics(coupon.code, durationDiscountCoupon),
      payment_type: booking?.paymentMethod?.card.funding ? `${booking?.paymentMethod.card.funding} card` : '',
      items: [getItemInfo(product, coupon, dateRange, durationDiscountCoupon, productPrice)],
    },
  })
}
export const trackAddShippingInfo = (
  product: Product,
  coupon: CouponCode,
  prices: CheckoutPrices,
  dateRange: DateRange
) => {
  const durationDiscountCoupon = getDurationDiscountCoupon(prices, dateRange)
  const productPrice = getTotalPriceFromProduct(prices) || 0
  analyticsService?.trackAddShippingInfo({
    event: 'add_shipping_info',
    ecommerce: {
      currency: product.price?.currency || '',
      value: productPrice,
      coupon: addCouponsToAnalytics(coupon.code, durationDiscountCoupon),
      items: [getItemInfo(product, coupon, dateRange, durationDiscountCoupon, productPrice)],
    },
  })
}

export const trackEnterCheckoutFlow = (
  product: Product,
  coupon: CouponCode,
  prices: CheckoutPrices,
  dateRange: DateRange
) => {
  const productPrice = getTotalPriceFromProduct(prices) || 0
  const durationDiscountCoupon = getDurationDiscountCoupon(prices, dateRange)
  analyticsService?.trackEnterCheckoutFlow({
    event: 'begin_checkout',
    ecommerce: {
      value: productPrice,
      currency: product.price?.currency?.toUpperCase() || '',
      coupon: addCouponsToAnalytics(coupon.code, durationDiscountCoupon),
      items: [getItemInfo(product, coupon, dateRange, durationDiscountCoupon, productPrice)],
    },
  })
}

const getItemInfo = (
  product: Product,
  coupon: CouponCode,
  dateRange: DateRange,
  durationDiscountCoupon: string,
  productPrice: number
) => {
  const productTypeName = analyticsService?.getProductTypeName(product.type).toLowerCase()
  const locationName = analyticsService?.getProductLocationName(product.building).toLowerCase()
  const couponDiscount = coupon.discountPercentage
    ? (coupon.discountPercentage / 100) * (product.price?.totalDue as number)
    : (product.price?.totalDue as number) - (coupon.discountAmount as number) / 100
  const itemName = analyticsService
    ?.generateItemName({
      productBuilding: locationName || '',
      productName: product.name,
      productType: product.type,
    })
    .toLowerCase()

  return {
    item_id: product.id.toString(),
    item_name: itemName || '',
    coupon: addCouponsToAnalytics(coupon.code, durationDiscountCoupon),
    discount: (product.price?.durationAmountOff as number) + couponDiscount,
    price: productPrice,
    quantity: 1,
    item_brand: 'your studio',
    item_category: productTypeName || '',
    item_category2: locationName || '',
    item_category3: 'instant book',
    item_category4: addRegionToAnalytics(analyticsService?.getProductRegion(product.building) as string) ?? '',
    item_category5: 'website purchase',
    location_id: product.locationId.toString(),
    product_name: itemName || '',
    product_type: productTypeName || '',
    product_id: product.id.toString(),
    product_capacity: product.capacity.toString(),
    product_hourly_rate: product.price?.unitPrice as number,
    product_source: product.source,
    booking_start_time: dayjs(dateRange.from).format('HH:mm A'),
    booking_end_time: dayjs(dateRange.to).format('HH:mm A'),
    booking_duration: dayjs(dateRange.to).diff(dayjs(dateRange.from), 'minutes') / 60,
    booking_date_selected: addDateToAnalytics(dateRange),
  }
}

const addCouponsToAnalytics = (couponCode: string, discountCoupon: string) => {
  if (couponCode && discountCoupon) {
    return `${couponCode} | ${discountCoupon}`
  } else if (couponCode || discountCoupon) {
    return couponCode || discountCoupon
  }

  return ''
}

const addDateToAnalytics = (dateRange: DateRange) => {
  const from = dayjs(dateRange.from)
  const to = dayjs(dateRange.to)
  if (from.isSame(to, 'day')) {
    return from.format('YYYY-MM-DD')
  } else {
    return `${from.format('YYYY-MM-DD')} | ${to.format('YYYY-MM-DD')}`
  }
}

const addRegionToAnalytics = (region: string) => {
  return region.split('-').join(' ').toLowerCase()
}

const getTotalPriceFromProduct = (prices: CheckoutPrices) => {
  return prices.fees.reduce((accumulator, currentObject) => {
    const feeValue = parseFloat((currentObject.fee as string).replace(/[^0-9.-]+/g, ''))
    return accumulator + feeValue
  }, 0)
}

export const trackForm = (data: GeneralForm) => {
  analyticsService?.trackForm({
    event: 'form_submit_ts',
    event_origin: 'front-end',
    event_vars: {
      form_name: data?.name?.toLowerCase() as string,
      form_type: data.type.toLowerCase(),
      form_id: data?.id as string,
      event_action: data.newsletter ? 'subscribed for updates' : 'did not subscribe for updates',
    },
  })
}

export const trackGeneralInquiryForm = (form: FormProps, state: FormFieldState, request: LeadRequest) => {
  const tourBuildingOption = state.inquireInterest?.value as DropdownOption
  const location_id =
    (tourBuildingOption?.id && analyticsService?.getProductLocationId(tourBuildingOption.id, 'tripleseat')?.trim()) ||
    'not available'

  analyticsService?.trackGeneralInquiryForm({
    event: 'contact_general_inquiry_form_submitted',
    event_origin: 'front-end',
    event_vars: {
      form_name: form?.name?.toLowerCase() as string,
      form_type: 'general inquiry',
      form_id: form?.id as string,
      event_action: state.newsletter?.value ? 'subscribed for updates' : 'did not subscribe for updates',
      property_title: request.locationName ? request.locationName.toLowerCase() : 'none selected',
      location_id,
    },
  })
}

export const trackPricingInquiryForm = (data: PriceInquiryForm) => {
  const location_id = analyticsService?.getProductLocationId(data.building, 'tripleseat')?.trim() || 'not available'

  analyticsService?.trackPricingInquiryForm({
    event: 'contact_pricing_inquiry_form_submitted',
    event_origin: 'front-end',
    event_vars: {
      product_name: data.product_name.toLowerCase().replace('_', ' ').replace('-', ' '),
      product_type: data.product_type.toLowerCase().replace('_', ' '),
      product_id: data.product_id,
      product_capacity: data.product_capacity,
      product_source: data.product_source.toLowerCase(),
      location_id,
    },
  })
}

export const trackTabClick = (data: TrackTabInfo) => {
  if (!data.titleDescriptor) return

  const titleDescriptor = data.titleDescriptor.toLocaleLowerCase()
  const url = new URL(data.windowLocationHref)
  const pageUrlClean = `${url.origin}${url.pathname}`

  analyticsService?.trackTabClick({
    event: 'tab_clicked',
    event_origin: 'front-end',
    event_vars: {
      title_descriptor: titleDescriptor,
      component_name: data.componentName,
      page_url_clean: pageUrlClean,
    },
  })
}

export const trackToggleClick = (data: TrackToggleInfo) => {
  const titleDescriptor = data.titleDescriptor.toLocaleLowerCase()
  const eventAction = data.value ? 'toggle turned on' : 'toggle turned off'
  const url = new URL(data.windowLocationHref)
  const pageUrlClean = `${url.origin}${url.pathname}`

  analyticsService?.trackToggleClick({
    event: 'toggle_clicked',
    event_origin: 'front-end',
    event_vars: {
      title_descriptor: titleDescriptor,
      component_name: data.componentName,
      event_action: eventAction,
      page_url_clean: pageUrlClean,
    },
  })
}

export const trackFormStart = (name: string, type: string, id?: string) => {
  analyticsService?.trackFormStart({
    event: 'form_start_ts',
    event_origin: 'front-end',
    event_vars: {
      form_name: name?.toLowerCase() ?? '',
      form_type: type?.toLowerCase() ?? '',
      form_id: id ?? '',
    },
  })
}

export const trackEmailSubscribeForm = (name: string, type: string, id?: string) => {
  analyticsService?.trackEmailSubscribeForm({
    event: 'email_subscribe',
    event_origin: 'front-end',
    event_vars: {
      form_name: name?.toLowerCase() ?? '',
      form_type: type?.toLowerCase() ?? '',
      form_id: id ?? '',
      event_action: 'subscribed for updates',
    },
  })
}

function formatLinkUrl(linkUrl: string): string {
  const isValidLinkUrl = (linkUrl: string) => {
    if (isValidHttpURL(linkUrl)) return true
    if (linkUrl === 'not applicable - map card opened') return true
    if (linkUrl.startsWith('mailto:')) return true
    if (linkUrl.startsWith('tel:')) return true
    return false
  }

  const tryFormatLinkAsContactType = (link: string) => {
    if (link.startsWith('mailto:')) {
      return 'contact email link'
    } else if (link.startsWith('tel:')) {
      return 'contact phone link'
    } else {
      return link
    }
  }

  if (isValidLinkUrl(linkUrl)) {
    return tryFormatLinkAsContactType(linkUrl)
  }

  return `${location.protocol}//${location.host.concat(linkUrl).replace('//', '/')}`
}

export const trackMapCardClick = (data: TrackMapCardClickInfo) => {
  const titleDescriptor = `${data.buildingName.toLocaleLowerCase()} | ${data.address
    .toLocaleLowerCase()
    .replace(/\n/g, ' | ')}`
  const url = new URL(data.windowLocationHref)
  const pageUrlClean = `${url.origin}${url.pathname}`
  const linkUrl = formatLinkUrl(data.linkUrl)

  analyticsService?.trackMapCardClick({
    event: 'map_clicked',
    event_origin: 'front-end',
    event_vars: {
      title_descriptor: titleDescriptor,
      event_action: data.eventAction,
      link_text: data.linkText.toLocaleLowerCase(),
      link_url: linkUrl,
      type_descriptor: data.typeDescriptor,
      component_name: data.componentName,
      page_url_clean: pageUrlClean,
    },
  })
}

export const trackSpaceCardPropertyResultClick = (
  data: TrackSpaceCardPropertyResultClickInfo,
  ab?: AbTestingContext | null
) => {
  const propertyTitle = `${data.building} | ${data.title} | ${data.spaceType}`
  const url = new URL(data.windowLocationHref)
  const pageUrlClean = `${url.origin}${url.pathname}`
  const linkUrl = formatLinkUrl(data.targetUrl)

  analyticsService?.trackPropertyResultClick({
    event: 'property_result_clicked',
    event_origin: 'front-end',
    event_vars: {
      property_title: propertyTitle.toLocaleLowerCase().replace('_', ' ').replace('-', ' '),
      property_type: data.spaceType.toLocaleLowerCase().replace('_', ' ').replace('-', ' '),
      business_region: data.businessRegion.toLocaleLowerCase().replace('_', ' ').replace('-', ' '),
      event_action: 'full card clicked',
      link_url: linkUrl,
      component_name: data.componentName || 'ml-space-card',
      page_url_clean: pageUrlClean,
      ...(ab
        ? {
            ab_component_name: data.componentName || 'ml-space-card',
            ab_variant: ab.variant,
            ab_cookie_name: ab.cookieName,
            ab_cookie_value: ab.value,
          }
        : {}),
    },
  })
}

export const trackBuildingCardPropertyResultClick = (data: TrackBuildingCardPropertyResultClickInfo) => {
  const url = new URL(data.windowLocationHref)
  const pageUrlClean = `${url.origin}${url.pathname}`
  const businessRegion = analyticsService?.getProductRegion(data.title.toLocaleLowerCase()) || ''
  const linkUrl = formatLinkUrl(data.targetUrl)

  analyticsService?.trackPropertyResultClick({
    event: 'property_result_clicked',
    event_origin: 'front-end',
    event_vars: {
      property_title: data.title.toLocaleLowerCase().replace('_', ' ').replace('-', ' '),
      property_type: 'building',
      business_region: businessRegion.toLocaleLowerCase().replace('_', ' ').replace('-', ' '),
      event_action: data.eventAction,
      link_url: linkUrl,
      component_name: 'building card',
      page_url_clean: pageUrlClean,
    },
  })
}

export const trackWorkspacePropertyResultClick = (data: TrackWorkspaceCardPropestyResultClickInfo) => {
  const getParamValueFromUrl = (url: string, param: string) => {
    const isTargetUrlComplete = url.includes('http') || url.includes('https') || url.includes('www')
    const tempTargetUrl = new URL(isTargetUrlComplete ? url : `http://dummy.com${url}`)
    return tempTargetUrl.searchParams.get(param)
  }

  const propertyType = getParamValueFromUrl(data.targetUrl, 'type') || ''
  const url = new URL(data.windowLocationHref)
  const pageUrlClean = `${url.origin}${url.pathname}`
  const linkUrl = formatLinkUrl(data.targetUrl)

  analyticsService?.trackPropertyResultClick({
    event: 'property_result_clicked',
    event_origin: 'front-end',
    event_vars: {
      property_title: data.propertyTitle.toLocaleLowerCase().replace('_', ' ').replace('-', ' '),
      property_type: propertyType.toLocaleLowerCase().replace('_', ' ').replace('-', ' '),
      business_region: '',
      event_action: 'full card clicked',
      link_url: linkUrl,
      component_name: 'workspace card',
      page_url_clean: pageUrlClean,
    },
  })
}

export const trackContentCardClick = (data: TrackContentCardClickInfo) => {
  const url = new URL(data.windowLocationHref)
  const pageUrlClean = `${url.origin}${url.pathname}`
  const linkUrl = formatLinkUrl(data.linkUrl)

  analyticsService?.trackContentCardClick({
    event: 'content_card_clicked',
    event_origin: 'front-end',
    event_vars: {
      content_headline: data.contentHeadline.toLocaleLowerCase(),
      content_section: data.contentSection?.toLocaleLowerCase() || 'none available',
      position_number: 'not applicable - content block',
      link_url: linkUrl,
      component_name: data.componentName,
      page_url_clean: pageUrlClean,
    },
  })
}

export const trackNavigationMenuClick = (data: TrackNavigationMenuClickInfo) => {
  const url = new URL(data.windowLocationHref)
  const pageUrlClean = `${url.origin}${url.pathname}`

  analyticsService?.trackNavigationMenuClick({
    event: 'navigation_menu_clicked',
    event_origin: 'front-end',
    event_vars: {
      navigation_menu_item_title: data.menuTitle.toLocaleLowerCase(),
      navigation_menu_heading: data.menuHeading.toLocaleLowerCase(),
      navigation_menu_type: data.menuType,
      link_url: formatLinkUrl(data.linkUrl),
      type_descriptor: data.menuLevel || 'menu_level_1', // for now we don't have a sub menu 'menu_level_2'
      component_name: data.componentName,
      page_url_clean: pageUrlClean,
    },
  })
}

export const trackNavigation = (data: TrackNavigationInfo) => {
  const urlObj: URL = new URL(data.pageUrl)
  const pageUrlClean = `${urlObj.origin}${urlObj.pathname}`

  const eventVars: any = {
    page_variant: data?.pageVariant,
    page_url_custom: data.pageUrl,
    page_url_clean: pageUrlClean,
    page_path_custom: data.pagePath,
    page_title_custom: data.pageTitle,
    page_referrer_custom: data.pageReferrer,
  }

  if (data.navigationVariant) {
    eventVars.navigation_variant = data.navigationVariant
  }
  if (data.footerVariant) {
    eventVars.footer_variant = data.footerVariant
  }

  analyticsService?.trackNavigation({
    event: 'pageview_custom',
    event_origin: 'front-end',
    event_vars: eventVars,
  })
}

export const setVariantIdCookie = (cookieName: string, variantId: string): void => {
  if (typeof document === 'undefined') return
  document.cookie = `${cookieName}=${variantId}; Path=/; Max-Age=31536000`
}

export const getVariantIdCookie = (cookieName: string): string | null => {
  if (typeof document === 'undefined') return null
  const cookie = document.cookie
  const rawCookie = cookie || ''
  const cookies = Object.fromEntries(rawCookie.split('; ').map((c) => c.split('=')))
  return cookies[cookieName]
}
